import {
  sanitizeProject,
  TRACKER_VERSION,
  type TrackerProject
} from "./model.js";

export const TRACKER_DRAFT_STORAGE_KEY = "am8.sound-tracker.project.v1";

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

function trackerFileStem(title: string): string {
  const normalized = title.trim().replace(/\s+/g, " ");
  const sanitized = normalized
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-")
    .replace(/[. ]+$/g, "")
    .trim();
  return sanitized === "" ? "Untitled Tracker Song" : sanitized;
}

export function projectToJson(project: TrackerProject): string {
  return JSON.stringify(project, null, 2);
}

export function downloadProjectJson(project: TrackerProject): string {
  const json = projectToJson(project);
  triggerDownload(new Blob([json], { type: "application/json" }), `${trackerFileStem(project.title)}.json`);
  return json;
}

export function downloadTrackerBytecode(project: TrackerProject, bytecode: Uint8Array): void {
  const copy = new Uint8Array(bytecode.byteLength);
  copy.set(bytecode);
  triggerDownload(new Blob([copy.buffer], { type: "application/octet-stream" }), `${trackerFileStem(project.title)}.am8trk`);
}

export async function readProjectFile(file: File): Promise<TrackerProject> {
  const text = await file.text();
  const parsed = JSON.parse(text) as { version?: number };
  if (parsed.version !== TRACKER_VERSION) {
    throw new Error(`unsupported tracker project version ${String(parsed.version)}`);
  }
  return sanitizeProject(parsed);
}

export function loadDraftProject(): TrackerProject | null {
  try {
    const raw = window.localStorage.getItem(TRACKER_DRAFT_STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    return sanitizeProject(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveDraftProject(project: TrackerProject): void {
  try {
    window.localStorage.setItem(TRACKER_DRAFT_STORAGE_KEY, projectToJson(project));
  } catch {
    return;
  }
}
